import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { validateHistoricProductDataViewDate } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	ErrorCode,
	LtMaxPrice as ApiLtMaxPrice,
	Product,
	ProductUpdate,
	Update,
	ValidationResult,
} from '~/Api/User/ProductGroup/LtMaxPrice';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { formatNumericHumanDate } from '~/DateTime';
import Calculation from '~/LtMaxPrice/Calculation/CalculationContainer';
import ParametersPanel, { Values as ReferenceValues } from '~/LtMaxPrice/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/LtMaxPrice/ProductsTable/ProductsTable';
import { parseDosage, parsePackageSize, PricedProduct, Product as GeneralProduct } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceValue } from './ProductsTable/Row/Row';
import styles from './styles.less';

interface Props {
	lithuanianProducts: ProductRow[];
	currency: string | null;
	detailProductId: string | null;
	detailProductTabName: string;
	group: UserProductGroup;
	header: ReactNode;
	ltMaxPrice: ApiLtMaxPrice | null;
	isFetching: boolean;
	otherProducts: ProductRow[];
	pricedProducts: Array<PricedProduct<Product>>;
	referenceBasketVersion: ReferenceBasketVersion | null;
	referencedProducts: ProductRow[];
	validationResult: ValidationResult | null;
	onCloseDetail(): void;
	onCurrencyChange(currency: string): void;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	onSave(group: UserProductGroup): Promise<void>;
	onShowDetail(productId: string | null, tabName: string): void;
	onUpdate(group: UserProductGroup, update: Update): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onDeleteItems(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
}

interface Values {
	product: { [productId: string]: ProductValues };
	reference: ReferenceValues;
}

interface State {
	lastSeenProductIdDetail: string | null;
	lastSelectedProductId: string | null;
	removeProduct: GeneralProduct | null;
	values: Values | null;
	checkedRows: GeneralProduct[];
}

export default class LtMaxPrice extends Component<Props, State> {
	public static defaultProps = {
		detailProductId: null,
		detailProductTabName: 'info',
		ltMaxPrice: null,
		validationResult: null,
	};

	public state: State = {
		lastSeenProductIdDetail: null,
		lastSelectedProductId: null,
		removeProduct: null,
		values: null,
		checkedRows: [],
	};

	public componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	public componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	public componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	private readonly createValues = memoizeOne((ltMaxPrice: ApiLtMaxPrice | null, values: Values | null): Values => {
		const historicProductDataViewDate = ltMaxPrice?.reference.historicProductDataViewDate ?? null;

		return {
			product:
				ltMaxPrice?.products.reduce((products: { [productId: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					if (currentValues !== undefined) {
						products[product.product.id] = currentValues;
						return products;
					}

					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);
					products[product.product.id] = {
						packageSize: {
							isValid: true,
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
						},
						packageSizeForSingleUnit: {
							isValid: true,
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
						},
						referencePrice: {
							isValid:
								product.referencePrices.length === 0 ||
								referencePrice !== undefined ||
								product.allowsEmptyCalculator.exFactory,
							value:
								referencePrice !== undefined
									? {
											value: referencePrice.calculatedPrice.type,
											parentValue: null,
											prompt: null,
									  }
									: null,
						},
						dosageStrength: {
							value: product.dosageStrengthForCalculation !== null ? String(product.dosageStrengthForCalculation) : '',
							isValid: true,
						},
						selected: product.selected,
					};
					return products;
				}, {}) ?? {},
			reference: values?.reference ?? {
				basketVersion: ltMaxPrice?.reference.referenceBasketVersion ?? '',
				packageSize: {
					value: String(ltMaxPrice?.reference.packageSize ?? ''),
					isValid: true,
				},
				dosageStrength: {
					value: String(ltMaxPrice?.reference.dosageStrength ?? ''),
					isValid: true,
				},
				historicProductDataViewDate: {
					value: historicProductDataViewDate !== null ? formatNumericHumanDate(historicProductDataViewDate) : '',
					dateValue: historicProductDataViewDate,
					isValid: true,
				},
			},
		};
	});

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	private readonly handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private readonly handleProductValuesChange = (
		item: Product,
		referencePrice: ReferencePriceValue | null,
		packageSize: string,
		packageSizeForSingleUnit: string,
		dosageStrength: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice.value?.value !== referencePrice?.value;

		this.setState(
			{
				values: {
					product: {
						...values.product,
						[item.product.id]: {
							packageSize: { value: packageSize, isValid: null },
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: null,
							},
							dosageStrength: { value: dosageStrength, isValid: null },
							referencePrice: {
								value: referencePrice,
								isValid:
									item.referencePrices.length === 0 || referencePrice !== null || item.allowsEmptyCalculator.exFactory,
							},
							selected: currentValue.selected,
						},
					},
					reference: values.reference,
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	private readonly referenceValuesChanged = (reference: ReferenceValues) => {
		const values = this.resolveValues();
		const invokeSave = reference.basketVersion !== values.reference.basketVersion;

		this.setState({ values: { ...values, reference } }, () => {
			this.update();
			if (invokeSave) {
				this.props.onSave(this.props.group);
			}
		});
	};

	private processValidationResult(validationResult: ValidationResult | null) {
		if (validationResult === null) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referencePackageSizeIsMissing,
			},
		};
		const productValues = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			if (error.productId === null || !(error.productId in productValues)) {
				return;
			}
			const values = productValues[error.productId];
			if (error.code === ErrorCode.requiredProductSettingsMissing) {
				productValues[error.productId] = {
					...values,
					packageSize: { ...values.packageSize, isValid: false },
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.illegalPriceTypeSelected) {
				productValues[error.productId] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	private readonly productSelected = (item: Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				lastSelectedProductId: item.product.id,
				values: { reference: values.reference, product: productValues },
			},
			this.update
		);
	};

	private resolveValues(): Values {
		const {
			props: { ltMaxPrice },
			state: { values },
		} = this;
		return this.createValues(ltMaxPrice, values);
	}

	private selectProductRange(
		productValues: { [productId: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	private readonly update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		const referencePackageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = referencePackageSize.isValid;

		const referenceDosageStrength = parseDosage(referenceValues.dosageStrength.value);
		referenceValues.dosageStrength.isValid = referenceDosageStrength.isValid;

		referenceValues.historicProductDataViewDate.isValid =
			referenceValues.historicProductDataViewDate.value === '' ||
			(referenceValues.historicProductDataViewDate.dateValue !== null &&
				validateHistoricProductDataViewDate(referenceValues.historicProductDataViewDate.dateValue));

		let hasValidationError =
			referencePackageSize.isValid === false ||
			referenceValues.historicProductDataViewDate.isValid === false ||
			referenceDosageStrength.isValid === false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSize, packageSizeForSingleUnit, dosageStrength } = values;

			const parsedPackageSize = parsePackageSize(packageSize.value);
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);
			const parsedDosageStrength = parseDosage(dosageStrength.value);

			hasValidationError =
				hasValidationError ||
				!referencePrice.isValid ||
				parsedPackageSize.isValid === false ||
				parsedPackageSizeForSingleUnit.isValid === false ||
				parsedDosageStrength.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: parsedPackageSize.isValid,
				},
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: parsedPackageSizeForSingleUnit.isValid,
				},
				dosageStrength: {
					...values.dosageStrength,
					isValid: parsedDosageStrength.isValid,
				},
			};

			products.push({
				dosageStrengthForCalculation: parsedDosageStrength.value,
				id: productId,
				packageSizeForCalculation: parsedPackageSize.value,
				packageSizeForSingleUnit: parsedPackageSizeForSingleUnit.value,
				priceTypeForCalculation: referencePrice.value?.value ?? null,
				selected: values.selected,
			});
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			this.props.onUpdate(this.props.group, {
				historicProductDataViewDate: referenceValues.historicProductDataViewDate.dateValue,
				packageSize: referencePackageSize.value,
				products,
				referenceBasketVersion: referenceValues.basketVersion,
				dosageStrength: referenceDosageStrength.value,
			});
		}
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	public render() {
		const {
			props: {
				header,
				ltMaxPrice,
				referenceBasketVersion,
				group,
				pricedProducts,
				lithuanianProducts,
				referencedProducts,
				otherProducts,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onCurrencyChange,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		const values = this.resolveValues();
		const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;
		const referenceDosageStrength = parseDosage(values.reference.dosageStrength.value).value;

		if (ltMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId !== null ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider id={detailProductId} ltMaxPrice={ltMaxPrice}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => {
											onShowDetail(productId, detailProductTabName);
										}}
										onSelectTab={(tabName) => {
											onShowDetail(detailProductId, tabName);
										}}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{ltMaxPrice !== null && referenceBasketVersion !== null ? <Calculation ltMaxPrice={ltMaxPrice} /> : null}

					<ParametersPanel
						group={group}
						ltMaxPrice={ltMaxPrice ?? null}
						values={values.reference}
						onChange={this.referenceValuesChanged}
						onSave={() => {
							onSave(group);
						}}
					/>

					{pricedProducts.length === 0 ? <ProductInstructions /> : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Lithuanian products</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(ltMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={lithuanianProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<Heading level={2}>Reference basket products</Heading>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							showReferenceDataSource
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(ltMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={referencedProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Other</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<ProductsTable
							disableSelection
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(ltMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={otherProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}
					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}

					{removeProduct !== null ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => {
								this.setState({ removeProduct: null });
							}}
							onConfirm={async () => {
								await onSave(group);
							}}
							onSuccess={() => {
								this.setState({ removeProduct: null });
							}}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}
}
